import { request } from './api'
import { dueDiligenceRefusal } from './requestRules'

// Baselines by group, type and tier, vendor groups, and the bulk send that
// proposes a baseline per vendor (release 3.4, M7; the mentor's point 6).
//
// # WHY THIS FILE EXISTS
//
// The server holds the rules: the resolver (packages/core/domain/baselines.go)
// decides each vendor's baseline and says why, the bulk send drafts one
// campaign per baseline through the campaign path, and a changed row is refused
// without a reason. The three screens (Settings, Baselines; Settings, Vendor
// groups; the bulk send at #/campaigns/new-batch) read the same answers, and
// the decisions they make before asking the server are plain functions here,
// so test/baselines.test.mjs can hold them without a browser: the rule table's
// rows, moving a rule within its kind, the vendors a filter picks, the state of
// a proposal row, the body of the send and the counts on the review.
//
// The calls live here and not in api.ts, which every lane edits: these screens
// are the only readers.

// ------------------------------------------------------------ the shapes

export type BaselineQuestionnaire = { id: string; code: string; name: string; versionId?: string; archived?: boolean }
export type BaselineModel = { id?: string; code?: string; versionId?: string }
export type VendorTypeOption = { code: string; label: string }

export type BaselineRule = {
  id: string
  ordinal: number
  kind: 'GROUP' | 'TYPE'
  groupId?: string
  groupName?: string
  vendorType?: string
  vendorTypeLabel?: string
  // Stored tier ordinals; 4 is Tier 1 Critical. Empty means every tier.
  tiers: number[]
  when: string
  questionnaire: BaselineQuestionnaire
  riskModel?: BaselineModel
  answerDays: number
  enabled: boolean
  problem?: string
  vendors: number
  updatedBy: string
  updatedAt: string
}

export type TierDefault = {
  tier: number
  label: string
  questionnaire: BaselineQuestionnaire
  riskModel: BaselineModel
  answerDays: number
  problem?: string
  vendors: number
}

export type BaselineRulesView = {
  version: number
  rules: BaselineRule[]
  tierDefaults: TierDefault[]
  noBaseline: number
  vendorTypes: VendorTypeOption[]
}

export type VendorGroup = {
  id: string
  name: string
  description?: string
  members: number
  rules: number
  createdBy: string
  createdAt: string
  retiredAt?: string
}

export type GroupMember = {
  vendorId: string
  vendorCode: string
  name: string
  status: string
  vendorType?: string
  tier?: number
  addedBy: string
  addedAt: string
}

export type BaselineSource = 'GROUP' | 'TYPE' | 'TIER' | 'NONE'

export type BaselineProposal = {
  source: BaselineSource
  ruleId?: string
  questionnaire: BaselineQuestionnaire
  riskModel: BaselineModel
  answerDays: number
  why: string
  // A rule read before the one that decided, passed over because its
  // questionnaire cannot be sent; the row's why already carries it.
  passedOver?: string
}

export type ProposalRow = {
  vendorId: string
  vendorCode: string
  name: string
  status: string
  requestApproved: boolean
  owner?: string
  tier?: number
  tierLabel?: string
  vendorType?: string
  vendorTypeLabel?: string
  groups: string[]
  groupIds: string[]
  proposal: BaselineProposal
  why: string
  eligible: boolean
  refusal?: { code: string; message: string }
}

export type ProposeAnswer = {
  proposals: ProposalRow[]
  missing: string[]
  questionnaires: BaselineQuestionnaire[]
  // The rules version these proposals were read under; the send names it back.
  rulesVersion: number
  // Set when the "every vendor" read stopped at this many, with the words.
  capped?: number
  note?: string
}

// proposedQuestionnaireId is what the proposal showed for the row ('' for
// nothing), so the server sends what the maker reviewed or refuses with
// BASELINES_CHANGED.
export type BatchVendor = { vendorId: string; proposedQuestionnaireId: string; questionnaireId?: string; reason?: string }
export type BatchCreate = { name: string; rulesVersion: number; dueOn?: string; reason?: string; vendors: BatchVendor[] }

// A vendor whose baseline moved between the proposal and the send.
export type BaselineMove = { vendorId: string; vendorCode: string; name: string; shown: string; now: string; why: string }
export type BatchCreated = {
  id: string
  status: string
  campaigns: { id: string; name: string; questionnaire: string; dueOn: string; recipients: number }[]
  skipped: { vendorId: string; reason: string }[]
  changed: number
  note: string
}

export type BatchCampaign = {
  id: string
  name: string
  status: string
  dueOn?: string
  questionnaireId: string
  questionnaireCode: string
  questionnaireName: string
  versionNo: number
  riskModelCode?: string
  recipients: number
  issued: number
  approvedBy?: string
}

export type BatchOverride = {
  vendorId: string
  vendorCode: string
  vendorName: string
  proposedQuestionnaire?: string
  proposedWhy?: string
  chosenQuestionnaire: string
  reason: string
  by: string
}

export type CampaignBatch = {
  id: string
  name: string
  dueOn?: string
  reason?: string
  status: string
  createdBy: string
  createdAt: string
  approvedBy?: string
  approvedAt?: string
  campaigns: BatchCampaign[]
  overrides: BatchOverride[]
}

export type BatchApproval = { who: string; youMay: boolean; why: string; capability: string }

export type BatchApproved = {
  id: string
  status: string
  approvedBy: string
  campaigns: { id: string; name: string; outcome: 'APPROVED' | 'REFUSED' | 'FAILED' | 'SKIPPED'; message?: string; counts?: Record<string, number> }[]
  counts: Record<string, number>
}

export type RuleWrite = {
  id?: string
  kind: 'GROUP' | 'TYPE'
  groupId?: string
  vendorType?: string
  tiers: number[]
  questionnaireId: string
  riskModelId?: string
  answerDays: number
  enabled: boolean
}

// ------------------------------------------------------------ the calls

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })

export const baselines = {
  rules: () => request<BaselineRulesView>('/v1/baseline-rules'),
  saveRules: (version: number, rules: RuleWrite[]) => request<BaselineRulesView>('/v1/baseline-rules', json('PUT', { version, rules })),

  groups: () => request<{ groups: VendorGroup[] }>('/v1/vendor-groups'),
  group: (id: string) => request<{ group: VendorGroup; members: GroupMember[] }>('/v1/vendor-groups/' + encodeURIComponent(id)),
  createGroup: (name: string, description: string) => request<{ group: VendorGroup }>('/v1/vendor-groups', json('POST', { name, description })),
  renameGroup: (id: string, name: string, description: string) =>
    request<{ group: VendorGroup }>('/v1/vendor-groups/' + encodeURIComponent(id), json('PUT', { name, description })),
  retireGroup: (id: string) => request<{ id: string; retired: boolean }>('/v1/vendor-groups/' + encodeURIComponent(id), { method: 'DELETE' }),
  addMembers: (id: string, vendorIds: string[]) =>
    request<{ group: VendorGroup; added: string[]; alreadyIn: string[]; missing: string[] }>(
      '/v1/vendor-groups/' + encodeURIComponent(id) + '/members',
      json('POST', { vendorIds }),
    ),
  removeMember: (id: string, vendorId: string) =>
    request<{ group: VendorGroup; removed: boolean }>(
      '/v1/vendor-groups/' + encodeURIComponent(id) + '/members/' + encodeURIComponent(vendorId),
      { method: 'DELETE' },
    ),

  proposeAll: (reason = '') => request<ProposeAnswer>('/v1/baselines/propose', json('POST', { all: true, reason })),
  createBatch: (body: BatchCreate) => request<BatchCreated>('/v1/campaign-batches', json('POST', body)),
  batch: (id: string) => request<{ batch: CampaignBatch; approval: BatchApproval }>('/v1/campaign-batches/' + encodeURIComponent(id)),
  approveBatch: (id: string) => request<BatchApproved>('/v1/campaign-batches/' + encodeURIComponent(id) + '/approve', { method: 'POST' }),
}

// ------------------------------------------------------------ the rule table

// The days a baseline gives a vendor to answer, as the table prints them.
export function daysWord(days: number): string {
  return days + (days === 1 ? ' day' : ' days')
}

export type RuleTableRow = {
  key: string
  order: string
  when: string
  baseline: string
  dueIn: string
  vendors: number
  // Why the resolver passes this row over, when it does.
  problem: string
  kind: 'GROUP' | 'TYPE' | 'TIER' | 'NONE'
}

// The table of the addendum: the rules in the order the resolver reads them,
// then each tier default, then the vendors nothing covers. Order runs on
// across all three, so "Order" is the order a vendor is read in.
export function ruleTableRows(view: BaselineRulesView): RuleTableRow[] {
  const rows: RuleTableRow[] = []
  let n = 0
  for (const r of orderRules(view.rules)) {
    n++
    rows.push({
      key: r.id,
      order: String(n),
      when: r.when,
      baseline: r.questionnaire.name,
      dueIn: daysWord(r.answerDays),
      vendors: r.vendors,
      problem: r.problem ?? '',
      kind: r.kind,
    })
  }
  for (const t of view.tierDefaults) {
    n++
    rows.push({
      key: 'tier-' + t.tier,
      order: String(n),
      when: t.label + ' (tier profile)',
      baseline: t.questionnaire.name || 'None set',
      dueIn: daysWord(t.answerDays),
      vendors: t.vendors,
      problem: t.problem ?? '',
      kind: 'TIER',
    })
  }
  rows.push({
    key: 'none',
    order: '',
    when: 'Nothing above',
    baseline: 'No baseline: chosen on the bulk send',
    dueIn: '',
    vendors: view.noBaseline,
    problem: '',
    kind: 'NONE',
  })
  return rows
}

// GROUP rules before TYPE rules, each by ordinal: the resolver's order.
export function orderRules<T extends { kind: string; ordinal?: number }>(rules: T[]): T[] {
  const rank = (k: string) => (k === 'GROUP' ? 0 : 1)
  return rules
    .map((r, i) => ({ r, i }))
    .sort((a, b) => rank(a.r.kind) - rank(b.r.kind) || (a.r.ordinal ?? a.i) - (b.r.ordinal ?? b.i) || a.i - b.i)
    .map((x) => x.r)
}

// The list as a save sends it, from the rules as read.
export function rulesAsWrites(rules: BaselineRule[]): RuleWrite[] {
  return orderRules(rules).map((r) => ({
    id: r.id,
    kind: r.kind,
    ...(r.kind === 'GROUP' ? { groupId: r.groupId } : { vendorType: r.vendorType }),
    tiers: r.kind === 'TYPE' ? [...r.tiers] : [],
    questionnaireId: r.questionnaire.id,
    ...(r.riskModel?.id ? { riskModelId: r.riskModel.id } : {}),
    answerDays: r.answerDays,
    enabled: r.enabled,
  }))
}

// Moves one rule up or down among the rules of its own kind. A group rule is
// always read before a type rule, so moving across that line would show an
// order the resolver does not use; at the edge of its kind it stays put.
export function moveRule(writes: RuleWrite[], index: number, dir: -1 | 1): RuleWrite[] {
  const to = index + dir
  const a = writes[index]
  const b = writes[to]
  if (!a || !b || a.kind !== b.kind) return writes
  const out = [...writes]
  out[index] = b
  out[to] = a
  return out
}

// Whether a rule can move that way at all, for the state of its button.
export function canMove(writes: RuleWrite[], index: number, dir: -1 | 1): boolean {
  return moveRule(writes, index, dir) !== writes
}

// Puts a new or edited rule in the list: a new group rule after the last
// group rule, a new type rule at the end; an edited rule where it was, unless
// its kind changed, when it moves to its new kind's end.
export function placeRule(writes: RuleWrite[], rule: RuleWrite, at: number | null): RuleWrite[] {
  if (at !== null && writes[at] && writes[at]!.kind === rule.kind) {
    const out = [...writes]
    out[at] = rule
    return out
  }
  const rest = at === null ? [...writes] : writes.filter((_, i) => i !== at)
  if (rule.kind === 'TYPE') return [...rest, rule]
  const lastGroup = rest.map((w) => w.kind).lastIndexOf('GROUP')
  return [...rest.slice(0, lastGroup + 1), rule, ...rest.slice(lastGroup + 1)]
}

// Tier ordinals most exposed first, the order every tier list reads in.
export const TIER_ORDINALS = [4, 3, 2, 1]

// ------------------------------------------------------------ the bulk send

export type VendorPick = {
  groups: string[]
  tiers: number[]
  types: string[]
  statuses: string[]
  owner: string
  q: string
}

export const EMPTY_PICK: VendorPick = { groups: [], tiers: [], types: [], statuses: [], owner: '', q: '' }

// The vendors a filter picks: in any of the groups, tiers, types and statuses
// chosen (each list empty is no filter), with the owner, and the name or code
// containing the text typed.
export function pickRows(rows: ProposalRow[], pick: VendorPick): ProposalRow[] {
  const q = pick.q.trim().toLowerCase()
  return rows.filter(
    (r) =>
      (pick.groups.length === 0 || r.groupIds.some((g) => pick.groups.includes(g))) &&
      (pick.tiers.length === 0 || (r.tier != null && pick.tiers.includes(r.tier))) &&
      (pick.types.length === 0 || (r.vendorType != null && pick.types.includes(r.vendorType))) &&
      (pick.statuses.length === 0 || pick.statuses.includes(r.status)) &&
      (pick.owner === '' || r.owner === pick.owner) &&
      (q === '' || r.name.toLowerCase().includes(q) || r.vendorCode.toLowerCase().includes(q)),
  )
}

// Whether a vendor is offered on the first step at all. Only a vendor the gate
// takes is offered (C5 of the 12 Oct plan): its request approved, not
// rejected, offboarded or awaiting approval, and somebody there to invite. A
// suspended vendor is offered, and asks for the send's reason.
export function offered(r: ProposalRow): boolean {
  if (r.refusal?.code === 'NO_CONTACT') return false
  const gate = dueDiligenceRefusal({ status: r.status, requestApproved: r.requestApproved }, 'a reason')
  return gate === null
}

// Whether the send needs its reason: a suspended vendor is in it.
export function needsSendReason(rows: ProposalRow[]): boolean {
  return rows.some((r) => dueDiligenceRefusal({ status: r.status, requestApproved: r.requestApproved }, '')?.code === 'SUSPENDED_NEEDS_REASON')
}

export type RowChoice = { questionnaireId?: string; reason?: string }

export type RowState =
  | { kind: 'proposed'; questionnaireId: string }
  | { kind: 'chosen'; questionnaireId: string }
  | { kind: 'changed'; questionnaireId: string }
  | { kind: 'needs-reason'; questionnaireId: string; message: string }
  | { kind: 'needs-choice'; message: string }

// One row of the proposal table, as the server's CheckBatchRow decides it:
// the proposal taken, a questionnaire chosen where nothing was proposed, a
// proposal replaced with a reason, a replacement still waiting for its
// reason, or a vendor nothing covers waiting for a choice.
export function rowState(r: ProposalRow, c: RowChoice | undefined, names: (id: string) => string = () => ''): RowState {
  const chosen = (c?.questionnaireId ?? '').trim()
  const proposed = r.proposal.source === 'NONE' ? '' : r.proposal.questionnaire.id
  if (chosen === '' && proposed === '') return { kind: 'needs-choice', message: 'No baseline covers this vendor. Choose the questionnaire it is sent.' }
  if (chosen === '' || chosen === proposed) return { kind: 'proposed', questionnaireId: proposed }
  if (proposed === '') return { kind: 'chosen', questionnaireId: chosen }
  if ((c?.reason ?? '').trim() === '') {
    const name = names(chosen) || 'another questionnaire'
    return {
      kind: 'needs-reason',
      questionnaireId: chosen,
      message: 'Say why this vendor is sent ' + name + ' and not ' + r.proposal.questionnaire.name + ' (' + r.proposal.why + ').',
    }
  }
  return { kind: 'changed', questionnaireId: chosen }
}

// The body of the send: every row that goes, with what its proposal showed,
// a questionnaire only where the person chose one and a reason only where they
// replaced a proposal; and the rules version the proposal was read under.
export function batchBody(
  name: string,
  dueOn: string,
  reason: string,
  rows: ProposalRow[],
  choices: Record<string, RowChoice>,
  rulesVersion: number,
): BatchCreate {
  const body: BatchCreate = { name: name.trim(), rulesVersion, vendors: [] }
  if (dueOn.trim()) body.dueOn = dueOn.trim()
  if (reason.trim()) body.reason = reason.trim()
  for (const r of rows) {
    const s = rowState(r, choices[r.vendorId])
    const v: BatchVendor = { vendorId: r.vendorId, proposedQuestionnaireId: r.proposal.source === 'NONE' ? '' : r.proposal.questionnaire.id }
    if (s.kind === 'chosen' || s.kind === 'changed' || s.kind === 'needs-reason') v.questionnaireId = s.questionnaireId
    if (s.kind === 'changed' || s.kind === 'needs-reason') v.reason = (choices[r.vendorId]?.reason ?? '').trim()
    body.vendors.push(v)
  }
  return body
}

export type ReviewLine = { questionnaireId: string; name: string; vendors: number; dueIn: string }
export type ReviewChange = { vendor: string; from: string; to: string; reason: string }

export type Review = {
  vendors: number
  baselines: ReviewLine[]
  changed: ReviewChange[]
  // Rows still waiting on the person: a reason or a choice.
  waiting: number
}

// The review step: how many vendors each baseline goes to, which rows the
// person changed and why, and whether anything still waits on them.
export function reviewOf(rows: ProposalRow[], choices: Record<string, RowChoice>, questionnaires: BaselineQuestionnaire[], dueOn = ''): Review {
  const nameOf = (id: string) => questionnaires.find((q) => q.id === id)?.name ?? rows.find((r) => r.proposal.questionnaire.id === id)?.proposal.questionnaire.name ?? id
  const by = new Map<string, ReviewLine>()
  const changed: ReviewChange[] = []
  let waiting = 0
  for (const r of rows) {
    const s = rowState(r, choices[r.vendorId], nameOf)
    if (s.kind === 'needs-choice' || s.kind === 'needs-reason') {
      waiting++
      continue
    }
    // A row that replaced its proposal, or chose where none was made, takes
    // the default days, as the server drafts it; only a taken proposal keeps
    // its rule's.
    const days = s.kind === 'proposed' ? r.proposal.answerDays : DEFAULT_ANSWER_DAYS
    const dueIn = dueOn ? dueOn : 'in ' + daysWord(days)
    const key = s.questionnaireId + '|' + dueIn
    const line = by.get(key) ?? { questionnaireId: s.questionnaireId, name: nameOf(s.questionnaireId), vendors: 0, dueIn }
    line.vendors++
    by.set(key, line)
    if (s.kind === 'changed') {
      changed.push({ vendor: r.name, from: r.proposal.questionnaire.name, to: nameOf(s.questionnaireId), reason: (choices[r.vendorId]?.reason ?? '').trim() })
    }
  }
  const baselines = [...by.values()].sort((a, b) => b.vendors - a.vendors || a.name.localeCompare(b.name))
  return { vendors: rows.length - waiting, baselines, changed, waiting }
}

// The days a send gives a row that is not on its proposal (domain
// DefaultAnswerDays).
export const DEFAULT_ANSWER_DAYS = 30

// The vendors a BASELINES_CHANGED refusal names, from its body.
export function movedVendors(body: unknown): BaselineMove[] {
  const v = (body as { vendors?: unknown } | null)?.vendors
  return Array.isArray(v) ? (v as BaselineMove[]) : []
}

// The line over the proposal table: "34 vendors, 3 baselines, 2 changed by you".
export function proposalSummary(review: Review): string {
  const parts = [
    review.vendors + (review.vendors === 1 ? ' vendor' : ' vendors'),
    review.baselines.length + (review.baselines.length === 1 ? ' baseline' : ' baselines'),
    review.changed.length + ' changed by you',
  ]
  if (review.waiting > 0) parts.push(review.waiting + ' waiting on you')
  return parts.join(', ')
}

// The three steps, as the step list names them.
export const BATCH_STEPS = [
  { key: 'vendors', label: 'Vendors' },
  { key: 'baselines', label: 'Baselines' },
  { key: 'review', label: 'Review' },
] as const

export type BatchStep = (typeof BATCH_STEPS)[number]['key']

// Whether a step may be opened yet: Baselines once a vendor is chosen, Review
// once no row waits on the person and the send has a name.
export function stepOpen(step: BatchStep, chosen: number, review: Review | null, name: string): boolean {
  if (step === 'vendors') return true
  if (step === 'baselines') return chosen > 0
  return chosen > 0 && review !== null && review.waiting === 0 && review.vendors > 0 && name.trim() !== ''
}
